// Админ data (04 §9).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isConfigured, isDemoMode, supabase } from '../../lib/supabase.js';
import { MOCK_QUERIES } from '../../lib/mockData-niche.js';

export interface ReviewItem {
  id: number;
  lead_id: number;
  issue_type: string;
  notes: string | null;
  created_at: string;
  business_name: string;
}

export interface JobHealth {
  job_name: string;
  last_success_at: string | null;
  last_error: string | null;
  meta: Record<string, unknown> | null;
}

export interface UsageRow {
  api_name: string;
  units: number;
}

export interface QueryRow {
  id: number;
  query_text: string;
  vertical_key: string;
  city_key: string;
  area_hint: string | null;
  cooldown_days: number;
  cooldown_jitter_days: number;
  last_run_at: string | null;
  total_found: number | null;
  new_found: number | null;
  active: boolean;
  // Provenance and yield (0033): draft = !active && approved_at null.
  batch_id: string | null;
  approved_at: string | null;
  generated_by: string;
  tier: string | null;
  runs: number;
  cumulative_new: number;
  zero_streak: number;
  skipped_filter: number;
}

export const QUERY_COLUMNS =
  'id, query_text, vertical_key, city_key, area_hint, cooldown_days, cooldown_jitter_days,' +
  ' last_run_at, total_found, new_found, active, batch_id, approved_at, generated_by, tier,' +
  ' runs, cumulative_new, zero_streak, skipped_filter';

export interface SettingRow {
  key: string;
  value: unknown;
}

const DEMO_REVIEWS: ReviewItem[] = [
  {
    id: 1,
    lead_id: 1,
    issue_type: 'domain_group_conflict',
    notes: 'Засечен втори уебсайт в социален профил',
    created_at: '2026-09-08T11:20:00Z',
    business_name: 'Дентално Студио Д-р Иванова',
  },
  {
    id: 2,
    lead_id: 3,
    issue_type: 'no_registry_match',
    notes: 'Несигурно съвпадение на ЕИК по име в Търговския регистър',
    created_at: '2026-09-08T14:40:00Z',
    business_name: 'Специализирана Клиника Бургас Дент',
  },
];

const DEMO_JOBS: JobHealth[] = [
  {
    job_name: 'pipeline',
    last_success_at: new Date(Date.now() - 2 * 3600_000).toISOString(),
    last_error: null,
    meta: { status: 'succeeded', run_id: 1 },
  },
  {
    job_name: 'nightly_sweep',
    last_success_at: '2026-09-09T03:00:00Z',
    last_error: null,
    meta: { processed: 48 },
  },
  {
    job_name: 'uptime_monitor',
    last_success_at: '2026-09-09T14:30:00Z',
    last_error: null,
    meta: { checked: 2 },
  },
  {
    job_name: 'log_retention_purge',
    last_success_at: '2026-09-08T00:00:00Z',
    last_error: null,
    meta: { purged: 120 },
  },
];

export function useReviewQueue() {
  return useQuery({
    queryKey: ['manual_review'],
    queryFn: async (): Promise<ReviewItem[]> => {
      if (!isConfigured || isDemoMode) {
        return DEMO_REVIEWS;
      }
      const { data, error } = await supabase
        .from('manual_review')
        .select('id, lead_id, issue_type, notes, created_at, leads(business_name)')
        .eq('resolved', false)
        .order('created_at', { ascending: true })
        .limit(200);
      if (error) throw new Error(error.message);

      return (data ?? []).map((row) => {
        const record = row as unknown as Record<string, unknown>;
        const lead = record['leads'] as { business_name?: string } | null;
        return {
          id: record['id'] as number,
          lead_id: record['lead_id'] as number,
          issue_type: record['issue_type'] as string,
          notes: (record['notes'] as string | null) ?? null,
          created_at: record['created_at'] as string,
          business_name: lead?.business_name ?? `#${String(record['lead_id'])}`,
        };
      });
    },
  });
}

export function useResolveReview() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      if (!isConfigured || isDemoMode) {
        const idx = DEMO_REVIEWS.findIndex((r) => r.id === id);
        if (idx !== -1) DEMO_REVIEWS.splice(idx, 1);
        return;
      }
      const { error } = await supabase
        .from('manual_review')
        .update({ resolved: true, resolved_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['manual_review'] });
      void client.invalidateQueries({ queryKey: ['manual_review_open'] });
    },
  });
}

export function useJobHealth() {
  return useQuery({
    queryKey: ['job_health'],
    refetchInterval: 60_000,
    queryFn: async (): Promise<JobHealth[]> => {
      if (!isConfigured || isDemoMode) {
        return DEMO_JOBS;
      }
      const { data, error } = await supabase
        .from('job_health')
        .select('job_name, last_success_at, last_error, meta')
        .order('job_name');
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as JobHealth[];
    },
  });
}

export function useUsageToday() {
  return useQuery({
    queryKey: ['api_usage_today'],
    refetchInterval: 60_000,
    queryFn: async (): Promise<UsageRow[]> => {
      if (!isConfigured || isDemoMode) {
        return [
          { api_name: 'google_places', units: 14 },
          { api_name: 'nominatim', units: 8 },
          { api_name: 'gemini_flash', units: 2 },
        ];
      }
      const since = new Date(Date.now() - 24 * 3600_000).toISOString();
      const { data, error } = await supabase
        .from('api_usage')
        .select('api_name, units')
        .gte('used_at', since)
        .limit(5000);
      if (error) throw new Error(error.message);

      const totals = new Map<string, number>();
      for (const row of data ?? []) {
        const name = row.api_name as string;
        totals.set(name, (totals.get(name) ?? 0) + (row.units as number));
      }
      return [...totals.entries()]
        .map(([api_name, units]) => ({ api_name, units }))
        .sort((a, b) => b.units - a.units);
    },
  });
}

export function useSearchQueries() {
  return useQuery({
    queryKey: ['search_queries'],
    queryFn: async (): Promise<QueryRow[]> => {
      if (!isConfigured || isDemoMode) return MOCK_QUERIES;
      const { data, error } = await supabase
        .from('search_queries')
        .select(QUERY_COLUMNS)
        .order('query_text')
        .limit(2000);
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as QueryRow[];
    },
  });
}

export function useToggleQuery() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) => {
      if (!isConfigured || isDemoMode) return;
      const { error } = await supabase.from('search_queries').update({ active }).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['search_queries'] }),
  });
}

export function useSettings() {
  return useQuery({
    queryKey: ['app_settings'],
    queryFn: async (): Promise<SettingRow[]> => {
      if (!isConfigured || isDemoMode) {
        return [
          { key: 'llm_billing_confirmed', value: true },
          {
            key: 'llm_chain',
            value: [
              { provider: 'gemini-cli', model: 'gemini-ultra', enabled: true },
              { provider: 'groq', model: 'llama-3.3-70b-versatile', enabled: true },
              { provider: 'cloudflare', model: '@cf/meta/llama-3.1-70b-instruct', enabled: true },
              { provider: 'zai', model: 'glm-4.7', enabled: true },
              { provider: 'gemini', model: 'gemini-2.5-flash', enabled: true },
            ],
          },
          { key: 'gemini_model', value: 'gemini-2.5-flash-lite' },
          { key: 'reaudit_days', value: 30 },
          { key: 'registry_autoapply_confidence', value: 80 },
          { key: 'identity_retry_days', value: 30 },
          { key: 'places_grid_max_calls', value: 250 },
          { key: 'harvest_retry_days', value: 14 },
        ];
      }
      const { data, error } = await supabase.from('app_settings').select('key, value').order('key');
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as SettingRow[];
    },
  });
}

export function useSetSetting() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      if (!isConfigured || isDemoMode) return;
      const { error } = await supabase.rpc('set_app_setting', { p_key: key, p_value: value });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['app_settings'] }),
  });
}
